package com.elan.edubridge.ui.member

import com.elan.edubridge.shared.di.SharedGraph
import com.elan.edubridge.shared.domain.model.UserRole
import com.elan.edubridge.ui.shell.icon
import com.elan.edubridge.ui.shell.renderPage
import kotlinx.browser.document
import kotlinx.coroutines.MainScope
import kotlinx.coroutines.launch
import org.w3c.dom.HTMLButtonElement

/**
 * 회원 뷰 7개 페이지가 공유하는 셸(topbar + 탭 4개 + 콘텐츠 래퍼, 가이드 §3.7).
 *
 * `ui/shell/AppHub.kt`의 `memberShellHtml()`은 이 lane이 실제 화면을 채우기 전까지의
 * 플레이스홀더였다(그 파일 클래스 코멘트가 명시). `ui/shell` 아래 전체는 이 lane의 소유 범위 밖이라
 * 직접 고치는 대신, 이미 styles.css에 있는 같은 루트 클래스(`eb-member-shell`,
 * `eb-member-topbar`, `eb-member-tabs`, `eb-member-content`)를 그대로 재사용해 동일한 결과를 낸다.
 * 통합 시점에 `/app/me` 라우트 소유권이 [com.elan.edubridge.ui.member.MemberRoutes]로 넘어오면
 * AppHub의 플레이스홀더는 자연히 대체된다.
 */
internal object MemberShell {
    private val scope = MainScope()
    private val accountRepository = SharedGraph.createAccountRepository()

    enum class Tab(val path: String, val label: String, val iconId: String) {
        HOME("/app/me", "홈", "icon-home"),
        SCHEDULE("/app/me/schedule", "일정", "icon-calendar"),
        NOTIFICATIONS("/app/me/notifications", "알림", "icon-notification"),
        SETTINGS("/app/me/settings", "설정", "icon-settings"),
    }

    /** 공지·과제·계정 삭제처럼 4탭 어디에도 속하지 않는 하위 화면은 `activeTab = null`을 넘긴다. */
    suspend fun render(context: MemberContext, activeTab: Tab?, bodyHtml: String) {
        val userId = context.session.account?.id.orEmpty()
        val unread = MemberNotifications.unreadCount(userId, MemberNotifications.loadAll(context))
        renderPage("eb-member-shell", shellHtml(context, activeTab, unread, bodyHtml))
        wireLogout()
    }

    private fun shellHtml(context: MemberContext, activeTab: Tab?, unread: Int, bodyHtml: String): String {
        val bellLabel = if (unread > 0) "알림 ${unread}건 읽지 않음" else "알림"
        val tabs = Tab.entries.joinToString("") { tab ->
            val active = tab == activeTab
            val activeClass = if (active) " eb-member-tabs__item--active" else ""
            val current = if (active) """ aria-current="page"""" else ""
            """<a class="eb-member-tabs__item$activeClass" href="${tab.path}"$current>${icon(tab.iconId, "md")}${tab.label}</a>"""
        }

        // 플랫폼 관리자(SUPER_ADMIN)는 회원 뷰에 있어도 관리자 콘솔 진입점이 항상 보여야 한다(§3.3).
        val adminLink = if (UserRole.SUPER_ADMIN in (context.session.account?.globalRoles ?: emptySet())) {
            """<a class="eb-button eb-button--ghost" href="/app/admin">플랫폼 관리</a>"""
        } else {
            ""
        }

        return """
            <header class="eb-member-topbar">
                <a class="eb-member-topbar__brand" href="/app/me">${icon("icon-bridge-logo", "lg")}EduBridge</a>
                <div class="eb-topbar__actions">
                    $adminLink
                    <a class="eb-icon-button" href="/app/me/notifications" aria-label="$bellLabel">
                        ${icon("icon-notification", "md")}${if (unread > 0) """<span class="eb-badge-dot" aria-hidden="true"></span>""" else ""}
                    </a>
                    <button class="eb-icon-button" id="member-logout-button" aria-label="로그아웃">${icon("icon-logout", "sm")}</button>
                </div>
            </header>
            <nav class="eb-member-tabs" aria-label="주 메뉴">$tabs</nav>
            <main id="content" class="eb-member-content" tabindex="-1">
                ${AppDownloadBanner.render()}
                $bodyHtml
            </main>
        """.trimIndent()
    }

    private fun wireLogout() {
        val button = document.getElementById("member-logout-button") as? HTMLButtonElement ?: return
        button.onclick = {
            // 로그인 화면 복귀는 세션 구독(Main.kt)이 처리한다 — publicOnly가 더 이상 인증되지 않음을 감지.
            scope.launch { accountRepository.logout() }
            null
        }
    }
}
